import React from 'react';
import { FUNNEL_DISCLAIMER } from './funnelVariants';

function TelegramInvitePanel({ variant }) {
  const previewItems =
    variant.previewItems?.slice(0, 3) || [
      'Market context before the setup',
      'Risk reminder before any decision',
      'Education first, action remains yours',
    ];

  return (
    <aside
      className="crypto-education-invite-panel"
      aria-label="Telegram group expectations"
      data-track-id="hero-invite-panel"
      data-track-hover="hero-invite-panel"
    >
      <div className="crypto-education-invite-panel-header">
        <span>What opens next</span>
        <strong>Free Telegram</strong>
      </div>
      <h2>{variant.signalLabel || 'Education notes'}</h2>
      <p>{variant.primaryLesson}</p>
      <ul>
        {previewItems.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      <div className="crypto-education-invite-guardrails" aria-label="Safety expectations">
        <span>No payment step</span>
        <span>No payment here</span>
        <span>No guaranteed outcomes</span>
      </div>
    </aside>
  );
}

function CommunityStatsPanel({ variant }) {
  const stats = variant.communityStats || [];

  return (
    <div className="crypto-education-community-panel" data-track-id="community-stats-panel">
      <div className="crypto-education-community-stats" aria-label="Community stats">
        {stats.map((stat) => (
          <div key={`${stat.value}-${stat.label}`}>
            <strong>{stat.value}</strong>
            <span>{stat.label}</span>
          </div>
        ))}
      </div>
      <p>
        Free Telegram education, daily market context, and risk-first reminders before the next
        decision.
      </p>
    </div>
  );
}

export function CryptoEducationFunnel({
  variant,
  rootRef,
  heroCtaRef,
  finalCtaRef,
  telegramCtaProps,
  telegramCtaLabel,
  showMobileCta,
  experimentAssignment,
}) {
  const heroBullets = variant.heroBullets || [
    'Join the free crypto education group',
    'Risk-first notes',
    'No payment to start',
  ];
  const proofSteps = variant.proofSteps || [
    ['01', 'Watch the note'],
    ['02', 'Run the check'],
    ['03', 'Join free'],
  ];
  const checkLabels = variant.checkLabels || ['Setup', 'Invalidation', 'Risk'];

  return (
    <main
      ref={rootRef}
      className="crypto-education-page"
      data-funnel-variant={variant.key}
      data-visual-funnel={variant.key}
      data-experiment-key={experimentAssignment?.experiment_key || undefined}
      data-experiment-variant={experimentAssignment?.experiment_variant || undefined}
      data-track-section="page"
    >
      <section className="crypto-education-hero" data-track-section="hero">
        <div className="crypto-education-hero-copy">
          <p
            className="crypto-education-eyebrow"
            data-track-id="hero-eyebrow"
            data-track-hover="hero-eyebrow"
          >
            {variant.eyebrow}
          </p>
          <h1 data-track-id="hero-headline">{variant.headline}</h1>
          <p className="crypto-education-intro" data-track-id="hero-intro">
            {variant.intro}
          </p>
          <div className="crypto-education-hero-bullets" aria-label="Funnel highlights">
            {heroBullets.map((bullet) => (
              <span key={bullet}>{bullet}</span>
            ))}
          </div>
          <div className="crypto-education-actions">
            <a
              {...telegramCtaProps}
              ref={heroCtaRef}
              className="crypto-education-primary-cta"
              data-track-id="hero-telegram-cta"
              data-track-hover="hero-telegram-cta"
              data-track-cta
            >
              <span>{telegramCtaLabel}</span>
            </a>
          </div>
        </div>

        <TelegramInvitePanel variant={variant} />
      </section>

      <section className="crypto-education-proof-strip" data-track-section="proof-strip">
        {proofSteps.map(([number, label]) => (
          <div key={number} data-track-id={`proof-strip-${number}`}>
            <span>{number}</span>
            <strong>{label}</strong>
          </div>
        ))}
      </section>

      <section className="crypto-education-preview" data-track-section="telegram-preview">
        <div className="crypto-education-section-heading">
          <p className="crypto-education-eyebrow">{variant.previewEyebrow || 'Inside the group'}</p>
          <h2 data-track-id="preview-heading">
            {variant.previewHeading || 'The free group teaches the thinking before the click.'}
          </h2>
          <p data-track-id="preview-summary">
            {variant.previewIntro ||
              'Open Telegram for education notes, market context, and risk reminders before making your own decision.'}
          </p>
        </div>

        <CommunityStatsPanel variant={variant} />
      </section>

      <section className="crypto-education-light" data-track-section="education-filter">
        <div className="crypto-education-section-heading">
          <p className="crypto-education-eyebrow">{variant.filterEyebrow || 'Decision filter'}</p>
          <h2 data-track-id="filter-heading">
            {variant.filterHeading || 'Before you react, run the check.'}
          </h2>
          <p data-track-id="filter-summary">{variant.primaryLesson}</p>
        </div>

        <div className="crypto-education-check-grid">
          {variant.checks.map((check, index) => (
            <article
              className="crypto-education-check"
              key={check}
              data-track-id={`check-${index + 1}`}
              data-track-hover={`check-${index + 1}`}
            >
              <span>{String(index + 1).padStart(2, '0')}</span>
              <strong>{checkLabels[index] || `Check ${index + 1}`}</strong>
              <p>{check}</p>
            </article>
          ))}
        </div>
      </section>

      <section
        className="crypto-education-process crypto-education-process--text-only"
        data-track-section="process"
      >
        <div className="crypto-education-process-copy">
          <p className="crypto-education-eyebrow">{variant.processEyebrow || 'Education loop'}</p>
          <h2 data-track-id="process-heading">
            {variant.processHeading || 'The free group teaches the thinking before the click.'}
          </h2>
          <div className="crypto-education-process-list">
            {variant.sections.map((section, index) => (
              <article
                key={section.title}
                data-track-id={`process-${index + 1}`}
                data-track-hover={`process-${index + 1}`}
              >
                <span>{String(index + 1).padStart(2, '0')}</span>
                <div>
                  <h3>{section.title}</h3>
                  <p>{section.copy}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="crypto-education-final" data-track-section="final-cta">
        <div>
          <p className="crypto-education-eyebrow">{variant.finalEyebrow || 'Free group'}</p>
          <h2 data-track-id="final-heading">
            {variant.finalHeading || 'Join free. Learn the setup. Decide with rules.'}
          </h2>
          <p data-track-id="final-copy">
            {variant.finalCopy ||
              'Get the market context and risk reminders first. No payment is requested on this page, and action remains yours.'}
          </p>
        </div>
        <a
          {...telegramCtaProps}
          ref={finalCtaRef}
          className="crypto-education-primary-cta"
          data-track-id="final-telegram-cta"
          data-track-hover="final-telegram-cta"
          data-track-cta
        >
          <span>{telegramCtaLabel}</span>
        </a>
        <p className="crypto-education-disclaimer" data-track-id="final-disclaimer">
          {FUNNEL_DISCLAIMER}
        </p>
      </section>

      <a
        {...telegramCtaProps}
        className={`crypto-education-mobile-cta${showMobileCta ? ' is-visible' : ''}`}
        aria-hidden={showMobileCta ? undefined : 'true'}
        tabIndex={showMobileCta ? undefined : -1}
        data-track-id="mobile-sticky-telegram-cta"
        data-track-hover="mobile-sticky-telegram-cta"
        data-track-cta
      >
        <span>{telegramCtaLabel}</span>
      </a>
    </main>
  );
}

export default CryptoEducationFunnel;
